import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { CgSpinner } from "react-icons/cg";
import CreditPackageCard from "./CreditPackageCard";
import { getAllCreditPackages } from "../../Api/api";
import { Elements } from "@stripe/react-stripe-js";
import { Modal } from "../ui/Modal";
import CreditPackageModal from "./CreditPackageModal";
import { loadStripe } from "@stripe/stripe-js";
import { AuthContext } from "../AuthProvider";
import { useNavigate } from "react-router";

export interface IMembership {
  _id: string;
  club_id: string;
  managed_by: string;
  name: string;
  is_member: boolean;
  start_date: number;
  end_date: number;
  discount: number;
  discount_validity: {
      [key: string]: {
          status: boolean;
          slot_id: string;
      };
  };
  information: string;
  price: number;
  period: string;
  binding_period: string;
  usp_1: string;
  usp_2: string;
  usp_3: string;
  usp_4: string;
  price_id: string;
  product_id: string;
  duration: number;
  timezone?: string;
  currency?: string;
  club_name?: string;
  club_address?: string;
  type: "Private" | "Public";
  sub_type?: string;
  hidden?: boolean;
  player_start_datetime?: number;
  player_end_datetime?: number;
  isLoading?: boolean;
  is_trial_available?: boolean;
  split_payment_fee?: any;
}

const CreditPackage = ({
  clubData,
  currency,
  timezone,
  tabName,
  clubID,
  setUserCredit
}: {
  clubData: any;
  currency: string;
  timezone: string;
  tabName: string;
  clubID: string;
  setUserCredit: Dispatch<SetStateAction<any>>;
}) => {
  const { t } = useTranslation();
  const currentUser = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [creditPackage, setCreditPackage] = useState<IMembership[]>([]);
  const [credits, setCredits] = useState<IMembership | {}>({});
  const [openCreditPackageModal, setOpenCreditPackageModal] = useState(false);
  const publishableKey = process.env.REACT_APP_STRIPE_KEY as string;
  const stripePromise = loadStripe(publishableKey);

  useEffect(() => {
      let mounted = true;

      const fetchCreditPackage = async () => {
          if (mounted && currentUser && clubID && tabName === "credits") {
              setLoading(true);
              try {
                  const data = await getAllCreditPackages(clubID);
                  setCreditPackage(data || []);
              } catch (error) {
              } finally {
                  if (mounted && tabName === "credits") {
                      setLoading(false);
                  }
              }
          }
      };

      fetchCreditPackage();

      return () => {
          mounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubID, currentUser, credits]);

  return (
      <div className="text-[15px]">
          {!currentUser ? (
              <button className="hover:underline" onClick={()=> navigate("/sign-in")}>{t("loginToCheckCreditPackages")}</button>
          ) : (
              <>
                  {loading ? (
                      <div className="flex justify-center items-center mt-10">
                          <CgSpinner className="animate-spin text-[24px]" />
                      </div>
                  ) : (
                      <div
                          className={`${
                              creditPackage?.length ? "mt-7" : "mt-3"
                          } pb-4 md:pb-0 flex flex-row md:flex-col gap-6 overflow-y-auto`}
                      >
                          {creditPackage?.length > 0 ? (
                              <>
                                  {creditPackage?.map(
                                      (credits: any, index: number) => (
                                          <CreditPackageCard
                                              key={`${credits?.club_id}${index}`}
                                              timezone={timezone}
                                              currency={currency}
                                              {...credits}
                                              Handler={() => (
                                                  <button
                                                      className={`w-full min-h-[49px] text-white bg-secondary text-[13px] font-semibold rounded-full mt-4 cursor-pointer`}
                                                      onClick={() => {
                                                          setCredits(credits);
                                                          setOpenCreditPackageModal(
                                                              true
                                                          );
                                                      }}
                                                  >
                                                      {t("getAt")}{" "}
                                                      {credits.price}{" "}
                                                      {currency}
                                                  </button>
                                              )}
                                          />
                                      )
                                  )}
                              </>
                          ) : (
                              <p className="text-[15px]">
                                  {t("noCreditPackages")}
                              </p>
                          )}
                      </div>
                  )}
              </>
          )}
          <Elements stripe={stripePromise}>
              <Modal
                  open={openCreditPackageModal}
                  setOpen={setOpenCreditPackageModal}
              >
                  <CreditPackageModal
                      credits={credits}
                      clubData={clubData}
                      setOpenCreditPackageModal={setOpenCreditPackageModal}
                      setUserCredit={setUserCredit}
                  />
              </Modal>
          </Elements>
      </div>
  );
};

export default CreditPackage;